import i18next from 'i18next'
import { format, isToday, isYesterday } from 'date-fns'

export const formatMillisToMainTime = (millis: number): string => {
  if (Number.isNaN(millis)) return '0:00.000'

  return format(millis, 'm:ss.SSS')
}

export const formatSecondsToMinutesSeconds = (seconds: number): string =>
  format(new Date(seconds * 1000), 'm:ss')

/**
 * Formats a given number of bytes into a human-readable size string.
 * - "X B" if the size is less than 1 KB.
 * - "X.X KB" if the size is less than 1 MB.
 * - "X.X MB" if the size is less than 1 GB.
 * - "X.X GB" if the size is greater than or equal to 1 GB.
 *
 * @param bytes The number of bytes to format.
 * @returns A formatted size string.
 */
export const formatBytesToSize = (bytes: number): string => {
  if (bytes < 1024) return `${bytes} B`
  const kb = bytes / 1024
  if (kb < 1024) return `${kb.toFixed(1)} KB`
  const mb = kb / 1024
  if (mb < 1024) return `${mb.toFixed(1)} MB`
  const gb = mb / 1024
  return `${gb.toFixed(1)} GB`
}

/**
 * Formats a given Date object into a human-readable string.
 * - "Today, H:mm AM/PM" if the date is today.
 * - "Yesterday, H:mm AM/PM" if the date is yesterday.
 * - "MMM d, H:mm AM/PM" (e.g., "May 2, 9:15 AM") for other dates.
 *
 * @param date The Date object to format.
 * @returns A formatted date string.
 */
export const formatDateToTimestamp = (date: Date): string => {
  const timePart = format(date, 'h:mm') + ' ' + format(date, 'aa').toUpperCase()

  if (isToday(date)) {
    return `${i18next.t('ls_generic_today')}, ${timePart}`
  }

  if (isYesterday(date)) {
    return `${i18next.t('ls_generic_yesterday')}, ${timePart}`
  }

  const datePart = format(date, 'MMM d')
  return `${datePart}, ${timePart}`
}
